import { Entity } from '@backstage/catalog-model';
import { Content } from '@backstage/core-components';
import { PageShareTitle } from '../../lib/shareTitle';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { getStoredFunMode, translate } from '../i18n';

const SENTRY_PROJECT_SLUG_ANNOTATION = 'sentry.io/project-slug';

/** Same surface as the stock plugin's isSentryAvailable: annotated components. */
const isSentryAvailable = (entity: Entity) =>
  entity.kind.toLocaleLowerCase('en-US') === 'component' &&
  Boolean(entity.metadata.annotations?.[SENTRY_PROJECT_SLUG_ANNOTATION]);

/**
 * In-house Sentry entity tab, replacing the stock
 * @backstage-community/plugin-sentry tab (disabled via
 * entity-content:sentry/sentry-issues: false in app-config.yaml).
 *
 * Kept at path `/sentry` so the Home panel's per-service links
 * (`${entityRoute(entity)}/sentry`) still land here. The stock tab fetched a
 * 24h window and defaulted its filter to 24H, disagreeing with the Home
 * "Sentry issues" panel — SentryIssuesContent fetches a 14-day window and
 * defaults the picker to 14D so they match. Pinned after Issues and grouped
 * under Quality by app-config.yaml; kept in sync by sentryExtension.test.ts.
 */
export const sentryEntityContent = EntityContentBlueprint.make({
  name: 'sentry-issues',
  params: {
    path: '/sentry',
    // Non-component config (evaluated once at extension registration), so
    // this reads the fun-mode flag directly instead of the useT() hook — see
    // the i18n module's non-component-code note. "Sentry" is a brand name,
    // so its `en`/`fun` values are identical either way.
    title: translate('sentry.entityContent.title', getStoredFunMode()),
    filter: isSentryAvailable,
    loader: () =>
      import('./SentryIssuesContent').then(m => (
        <PageShareTitle title="Sentry issues">
          <Content>
            <m.SentryIssuesContent />
          </Content>
        </PageShareTitle>
      )),
  },
});
